import type { Component } from 'vue'
import type { RouteRecordRaw } from 'vue-router'

import type { DocType } from '@/constants/doctypes'
import type { Permission } from '@/types/external/2work/api/response/getDoctypePermissions'

import {
  HAS_CREATE_VIEW_ONE_PERMISSION_OF,
  HAS_DETAIL_VIEW_ONE_PERMISSION_OF,
  HAS_LIST_VIEW_ONE_PERMISSION_OF,
  PERMISSIONS,
} from '@/constants/permissions'

type ViewLoader = () => Promise<Component>

interface DoctypeListDetailRoutesOptions {
  /**
   * Route name of the detail view, e.g. `uomDetail`.
   */
  detailName: string
  doctype: DocType
  /**
   * Route name of the list view, e.g. `uom`.
   */
  homeName: string
  /**
   * Path segment of the doctype root, e.g. `uom`.
   */
  path: string
  views: {
    detail: ViewLoader
    list: ViewLoader
  }
  /**
   * Path segment of the detail view. Defaults to `:id`.
   */
  detailPath?: string
  /**
   * Additional doctype permissions required by the list view.
   */
  extraListDoctypes?: Record<string, Permission[]>
  /**
   * Additional child routes of the doctype root, e.g. kanban or inbox views.
   */
  extraRoutes?: RouteRecordRaw[]
  /**
   * Passed through as `meta.if_owner` of the list route.
   */
  ifOwner?: boolean
}

interface DoctypeRoutesOptions extends Omit<DoctypeListDetailRoutesOptions, 'views'> {
  /**
   * Route name of the create view, e.g. `new-uom`.
   */
  createName: string
  /**
   * Path segment of the create view, e.g. `new-uom`.
   */
  createPath: string
  views: {
    create: ViewLoader
    detail: ViewLoader
    list: ViewLoader
  }
}

interface SingleViewRoutesOptions {
  /**
   * Doctype permissions required by the view.
   */
  doctypes: Record<string, Permission[]>
  /**
   * Route name of the view.
   */
  name: string
  /**
   * Path segment of the route root.
   */
  path: string
  view: ViewLoader
  /**
   * Passed through as `meta.if_owner`.
   */
  ifOwner?: boolean
}

interface SingleViewRoutes {
  home: RouteRecordRaw
  root: RouteRecordRaw
}

interface DoctypeListDetailRoutes {
  detail: RouteRecordRaw
  home: RouteRecordRaw
  root: RouteRecordRaw
}

interface DoctypeRoutes extends DoctypeListDetailRoutes {
  create: RouteRecordRaw
}

/**
 * Build the route of a standalone view (dashboard, settings page) plus its root
 * record.
 */
const createSingleViewRoutes = (options: SingleViewRoutesOptions): SingleViewRoutes => {
  const home: RouteRecordRaw = {
    component: options.view,
    meta: {
      doctypes: options.doctypes,
      ...(options.ifOwner === undefined ? {} : { if_owner: options.ifOwner }),
    },
    name: options.name,
    path: '',
  }

  return { home, root: { children: [home], path: options.path } }
}

/**
 * Build the list and detail route of an external doctype view plus their root
 * record, for doctypes without a create view.
 */
const createDoctypeListDetailRoutes = (
  options: DoctypeListDetailRoutesOptions,
): DoctypeListDetailRoutes => {
  const home: RouteRecordRaw = {
    component: options.views.list,
    meta: {
      doctypes: {
        [options.doctype]: HAS_LIST_VIEW_ONE_PERMISSION_OF,
        ...options.extraListDoctypes,
      },
      ...(options.ifOwner === undefined ? {} : { if_owner: options.ifOwner }),
    },
    name: options.homeName,
    path: '',
  }

  const detail: RouteRecordRaw = {
    component: options.views.detail,
    meta: {
      detail: { doctype: options.doctype, perm_type: PERMISSIONS.READ },
      doctypes: { [options.doctype]: HAS_DETAIL_VIEW_ONE_PERMISSION_OF },
    },
    name: options.detailName,
    path: options.detailPath ?? ':id',
  }

  return {
    detail,
    home,
    root: { children: [home, detail, ...(options.extraRoutes ?? [])], path: options.path },
  }
}

/**
 * Build the standard list / detail / create route triple of an external doctype
 * view plus their root record, so a doctype only declares its names, paths and
 * view components.
 */
const createDoctypeRoutes = (options: DoctypeRoutesOptions): DoctypeRoutes => {
  const { detail, home } = createDoctypeListDetailRoutes(options)

  const create: RouteRecordRaw = {
    component: options.views.create,
    meta: { doctypes: { [options.doctype]: HAS_CREATE_VIEW_ONE_PERMISSION_OF } },
    name: options.createName,
    path: options.createPath,
  }

  return {
    create,
    detail,
    home,
    root: {
      children: [home, detail, create, ...(options.extraRoutes ?? [])],
      path: options.path,
    },
  }
}

export {
  createDoctypeListDetailRoutes,
  createDoctypeRoutes,
  createSingleViewRoutes,
  type DoctypeListDetailRoutes,
  type DoctypeListDetailRoutesOptions,
  type DoctypeRoutes,
  type DoctypeRoutesOptions,
  type SingleViewRoutes,
  type SingleViewRoutesOptions,
}
